/**
 * Cart Validation for Customizer Products
 * Validates cart items before allowing checkout
 * 
 * The primary identifier for matching relationships is `pairId` - a unique identifier that
 * links parent products with their child products (logos, embroidery, etc.).
 * `variant_id` is only used for type verification (to distinguish front vs back children)
 * 
 * CHILD PRODUCT VALIDATION:
 *    - For items with `_isCustomizerChild === true`:
 *      - Finds parent by matching `pairId` only
 *      - If no parent found with matching `pairId` → marks as orphaned for removal
 * 
 * PARENT PRODUCT VALIDATION:
 *    - For items with `_customizerProduct === 'Yes'`:
 *      
 *      a) FRONT CHILD CHECK:
 *         - If `_customizerFrontId` exists → finds child with matching `pairId` and `variant_id`
 *         - Front customization is always a logo/patch
 *      
 *      b) BACK CHILD CHECK:
 *         - If `_customizerBackId` exists → finds child with matching `pairId` and `variant_id`
 *         - Validates embroidery fields if back exists:
 *           * Monogrammed: requires `Monogram Font` and `Monogram Thread Color`
 *           * Regular: requires `Embroidery Font`, `Thread Color`, and `Embroidery Text`
 *      
 *      c) LOGO CHILD CHECK:
 *         - If front exists → uses front child as logo child
 *         - If no front but logo exists → finds logo child by `pairId` and product type
 * 
 * ERRORS:
 *    - Validation errors are stored with `item.key` and `pairId`
 *    - Errors are displayed on DOM elements by matching `property-_pairid:{pairId}` class
 */

class CartValidation {
  constructor() {
    this.checkoutButtons = [];
    this.init();
  }

  init() {
    // Find all checkout buttons
    this.findCheckoutButtons();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Validate on cart updates
    this.setupCartUpdateListener();
    
    // Initial validation
    this.validateCart();
  }

  findCheckoutButtons() {
    this.checkoutButtons = [];
    
    // Rebuy SmartCart checkout button
    const rebuyCheckout = document.querySelector('.rebuy-cart__checkout-button');
    if (rebuyCheckout) {
      this.checkoutButtons.push(rebuyCheckout);
    }

    // Main cart page checkout button
    const mainCheckout = document.getElementById('checkout');
    if (mainCheckout) {
      this.checkoutButtons.push(mainCheckout);
    }

    // Cart drawer checkout button
    const drawerCheckout = document.getElementById('CartDrawer-Checkout');
    if (drawerCheckout) {
      this.checkoutButtons.push(drawerCheckout);
    }

    // Additional checkout buttons (dynamic checkout)
    const additionalButtons = document.querySelectorAll('.additional-checkout-buttons button, [name="checkout"]');
    additionalButtons.forEach(btn => {
      if (!this.checkoutButtons.includes(btn)) {
        this.checkoutButtons.push(btn);
      }
    });
  }

  setupEventListeners() {
    this.checkoutButtons.forEach(button => {
      // Use capture phase to intercept before Rebuy's handlers
      button.addEventListener('click', (e) => {
        this.handleCheckoutClick(e);
      }, true); // Use capture phase to run before other handlers
    });

    // Also intercept form submissions
    const cartForm = document.getElementById('cart');
    const drawerForm = document.getElementById('CartDrawer-Form');
    
    if (cartForm) {
      cartForm.addEventListener('submit', (e) => {
        this.handleCheckoutClick(e);
      });
    }

    if (drawerForm) {
      drawerForm.addEventListener('submit', (e) => {
        this.handleCheckoutClick(e);
      });
    }
  }

  setupCartUpdateListener() {
    // Listen for Rebuy SmartCart events that actually fire
    document.addEventListener('rebuy:smartcart.ready', () => {
      setTimeout(() => {
        this.findCheckoutButtons();
        this.setupEventListeners();
        this.validateCart();
      }, 200);
    });

    document.addEventListener('rebuy:smartcart.show', () => {
      setTimeout(() => {
        this.findCheckoutButtons();
        this.setupEventListeners();
        this.validateCart();
      }, 300);
    });

    document.addEventListener('rebuy:cart.change', () => {
      setTimeout(() => {
        this.findCheckoutButtons();
        this.setupEventListeners();
        this.validateCart();
      }, 200);
    });

    // Listen for Rebuy line item removal
    document.addEventListener('rebuy:smartcart.line-item-removed', () => {
      setTimeout(() => {
        this.findCheckoutButtons();
        this.setupEventListeners();
        this.validateCart();
      }, 500);
    });

    // Listen for Rebuy line item decrease (might be removal if quantity goes to 0)
    document.addEventListener('rebuy:smartcart.line-item-decrease', () => {
      setTimeout(() => {
        this.findCheckoutButtons();
        this.setupEventListeners();
        this.validateCart();
      }, 500);
    });


    // Listen for line item increase (same approach as cart.js)
    document.addEventListener('rebuy:smartcart.line-item-increase', () => {
      setTimeout(() => {
        this.findCheckoutButtons();
        this.setupEventListeners();
        this.validateCart();
      }, 100);
    });
  }

  async handleCheckoutClick(e) {
    const validation = await this.validateCart();
    
    if (!validation.isValid) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    }

    // If validation passes, allow normal checkout flow
    return true;
  }

  async validateCart() {
    try {
      const response = await fetch('/cart.json');
      const cart = await response.json();
      const errors = [];
      const items = cart.items || [];
      const itemErrors = new Map();
      const orphanedItems = {};

      items.forEach((item) => {
        const props = item.properties || {};
        const isCustomizerProduct = props._customizerProduct === 'Yes';
        const isCustomizerChild = props._isCustomizerChild === 'true' || props._isCustomizerChild === true;
        let parentFound = false;
        // Validate child products (check if they have a parent)
        if (isCustomizerChild) {
          const childPairId = props._pairId;
          
          if (childPairId) {
            // Find parent in items array by pairId only
            const parentItem = items.find(p => 
              p.properties?._customizerProduct === 'Yes' &&
              p.properties?._pairId === childPairId
            );
            
            if (parentItem) {
              parentFound = true;
            }
          }

          if (!parentFound) {
            orphanedItems[item.key] = 0;
          }
        }

        // Validate parent products (check front/back children and embroidery fields)
        if (isCustomizerProduct) {
          const parentPairId = props._pairId;
          
          // Get all variables needed first
          const hasFront = !!props._customizerFrontId;
          const hasBack = !!props._customizerBackId;
          const hasLogo = !!(props.Logo || props._Logo || props['_Logo Data']);
          
          // Check front child - inline logic (no helper function)
          let hasFrontChild = false;
          if (hasFront && parentPairId) {
            const frontChild = items.find(cartItem => {
              const isVariantMatch = cartItem.variant_id?.toString() === props._customizerFrontId?.toString();
              const isChild = cartItem.properties?._isCustomizerChild === 'true' || cartItem.properties?._isCustomizerChild === true;
              const pairIdMatch = cartItem.properties?._pairId === parentPairId;
              return isVariantMatch && isChild && pairIdMatch;
            });
            hasFrontChild = !!frontChild;
          }
          
          // Check back child - inline logic (no helper function)
          let hasBackChild = false;
          if (hasBack && parentPairId) {
            const backChild = items.find(cartItem => {
              const isVariantMatch = cartItem.variant_id?.toString() === props._customizerBackId?.toString();
              const isChild = cartItem.properties?._isCustomizerChild === 'true' || cartItem.properties?._isCustomizerChild === true;
              const pairIdMatch = cartItem.properties?._pairId === parentPairId;
              return isVariantMatch && isChild && pairIdMatch;
            });
            hasBackChild = !!backChild;
          }
          
          // Check embroidery fields if back customization exists
          let hasEmbroideryError = false;
          if (hasBack) {
            // Check for monogrammed product (has monogram initials)
            const isMonogrammed = !!(props['Monogram First Initial'] || props['Monogram Middle Initial'] || props['Monogram Last Initial']);
            
            if (isMonogrammed) {
              // Monogrammed products need Monogram Font and Monogram Thread Color
              const hasMonogramFont = !!(props['Monogram Font']);
              const hasMonogramThreadColor = !!(props['Monogram Thread Color']);
              hasEmbroideryError = !hasMonogramFont || !hasMonogramThreadColor;
            } else {
              // Regular embroidery products need Embroidery Font, Thread Color, and Embroidery Text
              const hasEmbroideryFont = !!(props['Embroidery Font']);
              const hasThreadColor = !!(props['Thread Color']);
              const hasEmbroideryText = !!(props['Embroidery Text']);
              hasEmbroideryError = !hasEmbroideryFont || !hasThreadColor || !hasEmbroideryText;
            }
          }
          
          // Check logo (if front exists, logo should be the same as front since front is always a logo)
          // Only check logo separately if there's no front customization
          let hasLogoChild = false;
          if (hasFront) {
            // Front customization is always a logo, so use the front child as the logo child
            hasLogoChild = hasFrontChild;
          } else if (hasLogo && parentPairId) {
            // Only check for separate logo if there's no front customization - inline logic (no helper function)
            const logoName = props.Logo || props._Logo || '';
            const logoData = props['_Logo Data'] || props._Logo || '';
            const logoChild = items.find(cartItem => {
              const isChild = cartItem.properties?._isCustomizerChild === 'true' || cartItem.properties?._isCustomizerChild === true;
              const pairIdMatch = cartItem.properties?._pairId === parentPairId;
              const isLogoProduct = cartItem.product_type?.includes('Logo') || 
                                   cartItem.product_type?.includes('Patch') ||
                                   cartItem.product_type?.includes('DYO') ||
                                   cartItem.handle?.includes('logo') ||
                                   cartItem.handle?.includes('patch') ||
                                   (logoData && cartItem.sku === logoData) ||
                                   (logoName && cartItem.product_title === logoName);
              return isChild && pairIdMatch && isLogoProduct;
            });
            hasLogoChild = !!logoChild;
          }
          
          // Check if there are any validation failures
          const hasValidationError = (hasFront && !hasFrontChild) || 
                                    (hasBack && !hasBackChild) || 
                                    (hasBack && hasEmbroideryError) ||
                                    (hasLogo && !hasFront && !hasLogoChild);
          
          // If any validation fails, add error tracking
          if (hasValidationError) {
            errors.push('error'); // Just used to count errors
            itemErrors.set(item.key, {
              pairId: parentPairId || null
            });
          }
        }
      });

      // Automatically remove orphaned child items - inline logic in same loop context
      if (Object.keys(orphanedItems).length > 0) {
        // Remove orphaned child items from cart
        if (typeof window.routes !== 'undefined' && window.routes.cart_update_url && typeof fetchConfig !== 'undefined') {
          const body = JSON.stringify({ updates: orphanedItems });
          
          fetch(window.routes.cart_update_url, { ...fetchConfig(), ...{ body } })
            .then(response => response.text())
            .then(() => {
              // Re-validate after removal
              setTimeout(() => {
                this.validateCart();
              }, 300);
            })
            .catch(() => {
              // Silently fail - validation will run again on next cart update
            });
        }
        
        // Temporarily disable checkout while removing orphaned items
        // Validation will run again after removal completes
        this.updateCheckoutButtons(false);
        return {
          isValid: false,
          errors: ['orphaned_items_removing']
        };
      }

      const isValid = errors.length === 0;

      // Update UI based on validation result
      this.updateCheckoutButtons(isValid);
      this.updateItemErrors(itemErrors);

      return {
        isValid,
        errors
      };
    } catch (error) {
      // On error, allow checkout to proceed (fail open)
      this.updateCheckoutButtons(true);
      return {
        isValid: true,
        errors: []
      };
    }
  }

  updateCheckoutButtons(isValid) {
    this.checkoutButtons.forEach(button => {
      // Check if button is already wrapped
      const wrapper = button.parentElement?.classList.contains('cart-checkout-button-wrapper') 
        ? button.parentElement 
        : null;
      
      if (isValid) {
        button.disabled = false;
        button.classList.remove('cart-checkout-disabled');
        button.style.opacity = '';
        button.style.cursor = '';
        button.style.border = '';
        button.style.backgroundColor = '';
        button.removeAttribute('disabled');
        
        // Remove wrapper and tooltip if they exist
        if (wrapper) {
          const tooltip = wrapper.querySelector('.cart-checkout-tooltip');
          if (tooltip) tooltip.remove();
          // Move button back to wrapper's parent before removing wrapper
          const parent = wrapper.parentNode;
          if (parent) {
            parent.insertBefore(button, wrapper);
            wrapper.remove();
          } else {
            wrapper.replaceWith(button);
          }
        }
      } else {
        button.disabled = true;
        button.classList.add('cart-checkout-disabled');
        button.style.cursor = 'not-allowed';
        button.style.border = '1px solid #D2D2D2';
        button.style.backgroundColor = '#D2D2D2';
        
        // Only wrap if not already wrapped
        if (!wrapper) {
          const newWrapper = document.createElement('div');
          newWrapper.className = 'cart-checkout-button-wrapper';
          button.parentNode.insertBefore(newWrapper, button);
          newWrapper.appendChild(button);
          
          // Create tooltip
          const tooltip = document.createElement('div');
          tooltip.className = 'cart-checkout-tooltip';
          const tooltipMessage = window.cartValidationTooltipMessage || 'Some products have personalization errors. Please remove the affected products and add them again.';
          tooltip.textContent = tooltipMessage;
          newWrapper.appendChild(tooltip);
        } else {
          // Wrapper exists, just make sure tooltip exists
          let tooltip = wrapper.querySelector('.cart-checkout-tooltip');
          if (!tooltip) {
            tooltip = document.createElement('div');
            tooltip.className = 'cart-checkout-tooltip';
            const tooltipMessage = window.cartValidationTooltipMessage || 'Some products have personalization errors. Please remove the affected products and add them again.';
            tooltip.textContent = tooltipMessage;
            wrapper.appendChild(tooltip);
          } else {
            // Update existing tooltip with current message
            const tooltipMessage = window.cartValidationTooltipMessage || 'Some products have personalization errors. Please remove the affected products and add them again.';
            tooltip.textContent = tooltipMessage;
          }
        }
      }
    });
  }



  updateItemErrors(itemErrors) {
    // Clear existing item errors
    document.querySelectorAll('.cart-item-validation-error').forEach(el => el.remove());
    
    // Add error messages to each item using pairId to match DOM elements
    itemErrors.forEach((errorData, itemKey) => {
      const pairId = errorData.pairId;
      if (!pairId) {
        return;
      }
      
      // Find DOM element by looking for the class property-_pairid:{pairId}
      // Use attribute selector to match class containing the pairId
      const itemElements = document.querySelectorAll('li[role="listitem"]');
      let itemElement = null;
      
      for (const element of itemElements) {
        // Check if element has the class property-_pairid:{pairId}
        const hasPairIdClass = Array.from(element.classList).some(className => 
          className === `property-_pairid:${pairId}`
        );
        
        if (hasPairIdClass) {
          // Verify it's a customizer product
          const hasCustomizerProduct = element.classList.contains('property-key-_customizerproduct');
          if (hasCustomizerProduct) {
            itemElement = element;
            break;
          }
        }
      }
      
      if (itemElement) {
        const existingError = itemElement.querySelector('.cart-item-validation-error');
        if (!existingError) {
          this.addErrorToRebuyItem(itemElement, errorData);
        }
      }
    });
  }


  addErrorToRebuyItem(itemElement, itemData) {
    // Get error message from settings
    const errorMessage = window.cartValidationItemErrorMessage || 'Personalization error. Please remove this product and add it again.';
    
    // Create error element with simplified message - matching customizer-restrictions styles
    const errorDiv = document.createElement('div');
    errorDiv.className = 'cart-item-validation-error';
    errorDiv.innerHTML = `
        <p class="cart-item-error-text">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="7" cy="7" r="6.5" stroke="#D32F2F" stroke-linecap="round" stroke-linejoin="round"></circle>
            <path d="M7 4V7.5" stroke="#D32F2F" stroke-linecap="round" stroke-linejoin="round"></path>
            <circle cx="7" cy="9.5" r="0.5" fill="#D32F2F"></circle>
          </svg>
          ${this.escapeHtml(errorMessage)}</p>
    `;
    
    // Insert after the item info div
    const itemInfo = itemElement.querySelector('.rebuy-cart__flyout-item-info');
    
    if (itemInfo) {
      itemInfo.appendChild(errorDiv);
    } else {
      // Fallback: append to item element
      itemElement.appendChild(errorDiv);
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartValidation();
  });
} else {
  new CartValidation();
}